export const DURATION_UNITS = [
    'months',
    'days',
    'hours',
    'minutes',
    'seconds',
] as const;

export type Unit = (typeof DURATION_UNITS)[number];

export const DURATION_UNITS_CONVERSION: Record<Unit, number> = {
    seconds: 1,
    minutes: 60,
    hours: 60 * 60,
    days: 60 * 60 * 24,
    months: 60 * 60 * 24 * 30,
};

export const DURATION_UNITS_MAX = {
    seconds: 60,
    minutes: 60,
    hours: 24,
    days: 30,
    months: Infinity,
};

export function getUnitSeparator(u1: Unit, u2: Unit) {
    // Es. MM-dd
    if (u1 === 'months' && u2 === 'days') {
        return '-';
    }
    // Es. dd hh
    if (u1 === 'months' || u1 === 'days') {
        return ' ';
    }
    // Es. hh:mm (O altri casi strani tipo dd:ss)
    return ':';
}

export type DurationObj = { [k in (typeof DURATION_UNITS)[number]]: number };

export type DurationFormat = { [k in keyof DurationObj]?: boolean };

export function getActiveUnits(format: DurationFormat) {
    return DURATION_UNITS.filter((unit) => !!format[unit]);
}

export const DURATION_PLACEHOLDERS: Record<Unit, string> = DURATION_UNITS.reduce(
    (acc, unit) => ({ ...acc, [unit]: `${unit.substring(0, 1).toLocaleUpperCase()}${unit.substring(1)}Abbrev` }), {} as any
);

export const DURATION_UNITS_ORDER: Record<Unit, number> = DURATION_UNITS.reduce(
    (acc, unit, i) => ({ ...acc, [unit]: i }),
    {}
) as any;

export class Duration implements Readonly<DurationObj> {
    months = 0;
    days = 0;
    hours = 0;
    minutes = 0;
    seconds = 0;

    static fromSeconds(seconds: number, format: DurationFormat): Duration {
        if (isNaN(seconds) || seconds <= 0) {
            return new Duration({});
        }

        const state: Partial<DurationObj> = {};
        let iter = seconds;
        for (const unit of getActiveUnits(format)) {
            const result = Math.floor(iter / DURATION_UNITS_CONVERSION[unit]);
            state[unit] = result;
            iter -= result * DURATION_UNITS_CONVERSION[unit];
        }

        return new Duration({ ...format }, state);
    }

    static parse(value: string, format: DurationFormat): Duration | undefined {
        let regExpStr = '';

        const activeUnits = getActiveUnits(format);

        for (let i = 0; i < activeUnits.length; ++i) {
            const unit = activeUnits[i];
            if (unit === 'months') {
                regExpStr += '\\d+';
            } else {
                regExpStr += '\\d{2}';
            }
            const nextUnit = activeUnits[i + 1];
            if (nextUnit) {
                regExpStr += getUnitSeparator(unit, nextUnit);
            }
        }

        const regex = new RegExp(regExpStr);
        const matches = value.match(regex);
        if (!matches) {
            return undefined;
        }

        const [_, months, days, hours, minutes, seconds] = matches.map((n) =>
            parseInt(n.replace(/^0/g, ''), 10)
        );

        return new Duration(
            { ...format },
            {
                months: isNaN(months)
                    ? 0
                    : Math.min(months, DURATION_UNITS_MAX.months - 1),
                days: isNaN(days)
                    ? 0
                    : Math.min(days, DURATION_UNITS_MAX.days - 1),
                hours: isNaN(hours)
                    ? 0
                    : Math.min(hours, DURATION_UNITS_MAX.hours - 1),
                minutes: isNaN(minutes)
                    ? 0
                    : Math.min(minutes, DURATION_UNITS_MAX.minutes - 1),
                seconds: isNaN(seconds)
                    ? 0
                    : Math.min(seconds, DURATION_UNITS_MAX.seconds - 1),
            }
        );
    }

    constructor(
        private format: DurationFormat,
        state: Partial<DurationObj> = {}
    ) {
        this.patch(state);
    }

    isValid() {
        return (
            this.months >= 0 &&
            this.days >= 0 &&
            this.hours >= 0 &&
            this.minutes >= 0 &&
            this.seconds >= 0
        );
    }

    patch(state: Partial<DurationObj>) {
        for (const key in state) {
            if (Object.hasOwnProperty.call(state, key)) {
                const unit = key as Unit;
                const value = state[unit];
                if (typeof value === 'number') {
                    this[unit] = value;
                }
            }
        }

        this.realign();
    }

    asSeconds(): number {
        let sum = 0;
        for (const unit of DURATION_UNITS) {
            sum += this[unit] * DURATION_UNITS_CONVERSION[unit];
        }
        return sum;
    }

    toObject(): DurationObj {
        return {
            months: this.months,
            days: this.days,
            hours: this.hours,
            minutes: this.minutes,
            seconds: this.seconds,
        };
    }

    private realign(): void {
        for (let i = 1; i < DURATION_UNITS.length; ++i) {
            const unit = DURATION_UNITS[i];
            const prevUnit = DURATION_UNITS[i - 1];

            // Se l'unità è minore di 0 o maggiore del suo massimo, facciamo a mano l'operazione di riporto,
            //   nel caso in cui il formato specificato lo permetta.
            // Questa è la ragion per cui non è possibile usare Moment.Duration.
            //
            // Es.
            //   1h, -1m -> 0h, 59m
            //   0h, 120m -> 2h, 0m
            //   120m -> 120m (se il formato non specifica le ore).
            if (
                this.format[prevUnit] &&
                (this[unit] < 0 || this[unit] >= DURATION_UNITS_MAX[unit])
            ) {
                const diff = this[unit] % DURATION_UNITS_MAX[unit];
                const newValue =
                    diff < 0 ? DURATION_UNITS_MAX[unit] + diff : diff;
                const overflows = Math.floor(
                    this[unit] / DURATION_UNITS_MAX[unit]
                );

                this[unit] = newValue;
                this[prevUnit] += overflows;

                // Ripeti il processo, perché potrebbe essere andata in {over,under}-flow l'unità precedente
                return this.realign();
            }
        }
    }
}
